import React from "react";
import PageHeader from "../../../components/layout/PageHeader";
import { LoadPhase, SkeletonCard } from "../../../components/ui";
import type { PlanOrigin } from "../types/plans.types";

/**
 * The three cards of the builder, at the heights they stand at: the client bar,
 * the day's totals, and the meals with an option table under them.
 *
 * Heights rather than contents — what is inside each card is the builder's own
 * business once the plan lands.
 */
const CARDS = ["h-[74px]", "h-[62px]", "h-[330px]"];

export interface PlanDetailSkeletonProps {
  /** Where the plan was opened from, for the trail back out of it. */
  origin: PlanOrigin;
  /**
   * How long the plan has been coming. Only `slow` draws the builder; while it
   * is merely `waiting` there is the heading and the trail and nothing else,
   * so a plan that opens quickly is never preceded by a screenful of blocks.
   */
  phase: LoadPhase;
}

/**
 * The plan builder while the plan is being fetched.
 *
 * Drawn as the screen it becomes: the client bar, the totals, and the meals
 * below them. The trail back stays as words — where the plan was opened from is
 * known whether or not the plan itself ever arrives.
 */
const PlanDetailSkeleton: React.FC<PlanDetailSkeletonProps> = ({
  origin,
  phase,
}) => (
  <div className="flex h-full flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8 3xl:px-12 3xl:py-8">
    <PageHeader
      title={
        <>
          {/* What the heading is read out as, from the moment the request goes
              out. The block beside it is the plan's name, which nobody knows
              yet — and which is not drawn at all until the wait is worth
              admitting. */}
          <span className="sr-only">Loading plan…</span>
          <SkeletonCard
            phase={phase}
            className="h-5 w-[200px] sm:w-[260px] 3xl:h-6"
          />
        </>
      }
      breadcrumbs={[{ label: origin.label, to: origin.to }]}
    />

    {phase === "slow" && (
      <div className="mt-5 flex-1 min-h-0 overflow-hidden">
        <div className="flex flex-col gap-4">
          {CARDS.map((height) => (
            <SkeletonCard key={height} className={height} />
          ))}
        </div>
      </div>
    )}
  </div>
);

export default PlanDetailSkeleton;
